// AddReturnLines.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';

import Loading from '@components/Shared/Loader/Loading';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import { Card } from '@components/card/card';

import { useAppSelector } from '@store/hooks';

// ✅ Picker (Hook + Stable Modal) - (احتياطاً لو وصلت لاينات قديمة بدون صنف)
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

// ✅ API (lookup + preload details)
import { fetchItemsWithUomLookup, type ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';

// ✅ Totals (same as PO)
import { useDocumentCalculator } from '@Helper/documents/DocCalc/useDocumentCalculator';
// import { DocTotalsPanel } from '@Helper/documents/DocCalc/DocTotalsPanel';
import { num } from '@Helper/documents/DocCalc/round';

type ItemType = 'InventoryItem' | 'ExpenseItem' | 'FixedAssetItem' | 'ServiceItem' | 'UndefinedItem';

interface LinesProps {
  formik: FormikProps<any>;
  maxTableHeight?: string;
  isUpdate?: boolean;
}

const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const round2 = (v: any) => {
  const n = Number(v || 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

/** ✅ قراءة مرنة لحقول اللاين (لأن أسماء الحقول تختلف أحياناً حسب الـ API) */
const readItemId = (l: any) => String(l?.itemId ?? l?.itemID ?? l?.inventoryItemId ?? '');
const readUom = (l: any) => String(l?.uom ?? l?.unitOfMeasure ?? l?.unitOfMeasureCode ?? '');
const readQty = (l: any) => Number(l?.qty ?? l?.quantity ?? 0);

const readPrice = (l: any) => Number(l?.price ?? l?.unitPrice ?? l?.lastCostPrice ?? l?.costPrice ?? 0);
const readVatRate = (l: any) => Number(l?.vatRate ?? l?.vatPercent ?? l?.taxRate ?? 0);

const readDiscountRate = (l: any) => Number(l?.discountRate ?? 0);
const readDiscountAmount = (l: any) => Number(l?.discountAmount ?? l?.discountValue ?? 0);

const buildPurchasedQtyMap = (invoice: any) => {
  // ✅ key = itemId|uom  => purchased qty from ORIGINAL order (not the form lines)
  const map = new Map<string, number>();

  (invoice?.transactionsLinesList || []).forEach((l: any) => {
    const itemId = readItemId(l);
    const uom = readUom(l);
    if (!itemId || !uom) return;
    const key = `${itemId}|${uom}`;
    map.set(key, (map.get(key) || 0) + readQty(l));
  });

  return map;
};

const buildAvailableQtyMap = (invoice: any, returns: any[]) => {
  // ✅ key = itemId|uom
  const map = new Map<string, number>();
  (invoice?.transactionsLinesList || []).forEach((l: any) => {
    const itemId = readItemId(l);
    const uom = readUom(l);
    if (!itemId || !uom) return;
    const key = `${itemId}|${uom}`;
    map.set(key, (map.get(key) || 0) + readQty(l));
  });

  (returns || []).forEach((ret: any) => {
    (ret?.transactionsLinesList || []).forEach((l: any) => {
      const itemId = readItemId(l);
      const uom = readUom(l);
      if (!itemId || !uom) return;
      const key = `${itemId}|${uom}`;
      map.set(key, (map.get(key) || 0) - readQty(l));
    });
  });

  // ✅ لا نسمح بالسالب
  for (const [k, v] of map.entries()) map.set(k, Math.max(0, v));
  return map;
};

const AddReturnLines: React.FC<LinesProps> = ({ formik, maxTableHeight = '500px' }) => {
  const { values, setFieldValue, errors } = formik;
  const { i18n, t } = useTranslation();

  const inventoryId: string | null = values?.inventoryId ?? null;

  // ✅ order + previous returns are already loaded into the slice (see PurchaseReturn.tsx)
  // const { PurchaseReturnById: order } = useAppSelector(s => s.ReturnsPurchaseOrder);
  const { DetailsById: order } = useAppSelector(s => s.ReturnsPurchaseOrder);

  // ✅ Picker (optional)
  const { pickItemUom, modalProps } = useItemUomPicker();

  // ✅ Map لعرض أسماء الأصناف والوحدات + باركود
  const [detailsMap, setDetailsMap] = useState<Map<string, ItemWithUomRow>>(new Map());
  const [detailsLoading, setDetailsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // ✅ Barcode Scan (quick select + increment qty)
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const barcodeAbortRef = useRef<AbortController | null>(null);
  const [barcodeTerm, setBarcodeTerm] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);

  // =========================
  // ✅ Purchased qty (from ORIGINAL order)
  // =========================
  const purchasedQtyMap = useMemo(() => {
    return buildPurchasedQtyMap(order);
  }, [order]);

  // =========================
  // ✅ Available qty (from order - returns)
  // =========================
  const availableQtyMap = useMemo(() => {
    return buildAvailableQtyMap(order, order?.purTransactionsDtoList || []);
  }, [order]);

  const lines: any[] = values?.transactionsLinesList || [];

 

  // =========================
  // ✅ Calculate totals for return line
  // =========================
  const recalculateReturnLineTotals = (line: any) => {
    // ✅ IMPORTANT: totals based on qty (so useDocumentCalculator works)
    const qty = clamp(round2(line.qty), 0, Number(line.availableQtyReturn ?? 0));
    // const price = round2(line.price);
    const price = line.price;
    const vatRate = clamp(round2(line.vatRate), 0, 100);

    // sync alias (اختياري)
    line.qty = qty;
    line.ReturnQty = qty;

    if (!qty) {
      line.qty = 0;
      line.ReturnQty = 0;

      line.totalBeforeDiscount = 0;
      line.total = 0;
      line.vatAmount = 0;
      line.totalWithVat = 0;
      line.discountAmount = 0;
      return;
    }

    // 1) إجمالي قبل الخصم
    const totalBeforeDiscount = round2(price * qty);

    // 2) خصم: لو عندنا نسبة خصم استخدمها، وإلا خصم مبلغ يُوزّع نسبياً على الكمية
    const rateVal = clamp(round2(line.discountRate), 0, 100);

    let discountApplied = 0;

    if (rateVal > 0) {
      discountApplied = round2((totalBeforeDiscount * rateVal) / 100);
    } else {
      const origQty = Number(line._origQty ?? 0);
      const origAmt = Number(line._origDiscountAmount ?? 0);
      const perUnit = origQty > 0 ? origAmt / origQty : 0;
      discountApplied = round2(perUnit * qty);
    }

    discountApplied = clamp(discountApplied, 0, totalBeforeDiscount);

    // ✅ خزّن الخصم المعتمد للعودة
    line.discountAmount = round2(discountApplied);

    // 3) الصافي قبل الضريبة
    const amountAfterDiscount = round2(totalBeforeDiscount - discountApplied);

    // 4) الضريبة + الإجمالي شامل الضريبة
    const vatAmount = round2((amountAfterDiscount * vatRate) / 100);
    const totalWithVat = round2(amountAfterDiscount + vatAmount);

    line.vatRate = vatRate;

    line.totalBeforeDiscount = totalBeforeDiscount;
    line.total = amountAfterDiscount; // net before VAT
    line.vatAmount = vatAmount;
    line.totalWithVat = totalWithVat;
  };

  const setLineAt = (index: number, next: any) => {
    const updated = [...lines];
    updated[index] = next;
    setFieldValue('transactionsLinesList', updated);
  };

  // =========================
  // ✅ One-time normalize lines (add needed fields)
  // =========================
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;

    const srcLines = values?.transactionsLinesList || [];
    if (!Array.isArray(srcLines) || srcLines.length === 0) return;

    const normalized = srcLines.map((l: any) => {
      const itemId = readItemId(l);
      const uom = readUom(l);
      const key = itemId && uom ? `${itemId}|${uom}` : '';

      // ✅ purchased qty MUST come from order (not the form line)
      const purchasedQty = key ? Number(purchasedQtyMap.get(key) ?? readQty(l)) : readQty(l);

      return {
        ...l,

        itemId,
        uom,

        itemType: (l?.itemType || 'InventoryItem') as ItemType,

        // ✅ keep original item description for display (fallback)
        _itemDesc: String(l?.description || ''),

        // ✅ display only (الكمية المشتراة)
        _purchasedQty: purchasedQty,

        // ✅ selection / return qty
        selectedRow: false,

        // ✅ IMPORTANT: qty هو كمية الإرجاع (للحسابات)
        qty: 0,
        ReturnQty: 0, // alias فقط

        // ✅ remaining qty to return
        availableQtyReturn: key ? Number(availableQtyMap.get(key) ?? purchasedQty) : 0,

        // ✅ originals for proportional discount when needed
        _origQty: purchasedQty,
        _origDiscountAmount: readDiscountAmount(l),

        // ✅ ensure numeric fields exist for calculator
        price: readPrice(l),
        vatRate: readVatRate(l),
        discountRate: readDiscountRate(l),

        // ✅ totals for return (will be calculated)
        totalBeforeDiscount: 0,
        total: 0,
        vatAmount: 0,
        totalWithVat: 0,

        // ✅ reason/notes for return line (separate from item name)
        description: ''
      };
    });

    initRef.current = true;
    setFieldValue('transactionsLinesList', normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableQtyMap, purchasedQtyMap]);

  // ✅ Update availableQtyReturn & purchased qty when order / previous returns change (without losing user inputs)
  useEffect(() => {
    const src = values?.transactionsLinesList || [];
    if (!Array.isArray(src) || src.length === 0) return;

    const updated = src.map((l: any) => {
      const itemId = readItemId(l);
      const uom = readUom(l);
      const key = itemId && uom ? `${itemId}|${uom}` : '';

      const avail = key ? Number(availableQtyMap.get(key) ?? Number(l?.availableQtyReturn ?? 0)) : 0;

      // ✅ purchased qty comes from order map (fix purchased=0 issue)
      const purchased = key
        ? Number(purchasedQtyMap.get(key) ?? Number(l?._purchasedQty ?? l?._origQty ?? 0))
        : Number(l?._purchasedQty ?? l?._origQty ?? 0);

      const next = {
        ...l,
        itemId,
        uom,
        availableQtyReturn: avail,
        _purchasedQty: purchased,
        _origQty: purchased
      };

      const rq = clamp(round2(Number(next.qty || 0)), 0, avail);
      next.qty = rq;
      next.ReturnQty = rq;
      next.selectedRow = rq > 0;

      recalculateReturnLineTotals(next);
      return next;
    });

    setFieldValue('transactionsLinesList', updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableQtyMap, purchasedQtyMap]);

  // =========================
  // Display helpers
  // =========================
  const getInfo = (line: any) => {
    if (!line?.itemId || !line?.uom) return null;
    return detailsMap.get(`${line.itemId}|${line.uom}`) || null;
  };

  const getItemNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const name = isEn ? info?.name || info?.name2 : info?.name2 || info?.name;
    return name || line?._itemDesc || (line?.itemId ? `ID: ${line.itemId}` : '');
  };

  const getUomNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const uomName = isEn
      ? info?.unitOfMeasureName || info?.unitOfMeasureName2
      : info?.unitOfMeasureName2 || info?.unitOfMeasureName;
    return uomName || '';
  };

  // =========================
  // Handlers
  // =========================
  const handleToggleSelected = (index: number) => {
    const l = lines[index];
    if (!l) return;

    const available = Number(l.availableQtyReturn ?? 0);
    if (available <= 0) return;

    const next = { ...l, selectedRow: !l.selectedRow };

    if (!next.selectedRow) {
      next.qty = 0;
      next.ReturnQty = 0;
    } else {
      if (!Number(next.qty) && available > 0) next.qty = 1;
      next.ReturnQty = next.qty;
    }

    recalculateReturnLineTotals(next);
    setLineAt(index, next);
  };

  const handleReturnQtyChange = (index: number, value: any) => {
    const l = lines[index];
    if (!l) return;

    const available = Number(l.availableQtyReturn ?? 0);
    const qty = clamp(round2(value), 0, available);

    const next = { ...l, selectedRow: qty > 0, qty, ReturnQty: qty };
    recalculateReturnLineTotals(next);
    setLineAt(index, next);
  };

  const handleDescChange = (index: number, desc: string) => {
    const l = lines[index];
    if (!l) return;
    setLineAt(index, { ...l, description: desc });
  };

  // =========================
  // ✅ Load missing details (names/barcodes/uoms) - same idea as PO
  // =========================
  const linesKey = useMemo(() => {
    return (lines || []).map(l => `${l?.itemType || ''}:${l?.itemId || ''}:${l?.uom || ''}`).join('|');
  }, [lines]);

  useEffect(() => {
    const run = async () => {
      if (!inventoryId) return;

      const valid = (lines || []).filter(l => l?.itemId && l?.uom);
      if (valid.length === 0) return;

      const missingByType = new Map<string, Set<string>>();
      for (const l of valid) {
        const key = `${l.itemId}|${l.uom}`;
        if (detailsMap.has(key)) continue;

        const type = String(l.itemType || 'InventoryItem');
        if (!missingByType.has(type)) missingByType.set(type, new Set());
        missingByType.get(type)!.add(String(l.itemId));
      }

      if (missingByType.size === 0) return;

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setDetailsLoading(true);
      try {
        const merged = new Map(detailsMap);

        for (const [type, idsSet] of missingByType.entries()) {
          const itemIds = Array.from(idsSet);
          for (const idsChunk of chunk(itemIds, 200)) {
            const resp = await fetchItemsWithUomLookup(
              { InventoryId: inventoryId, Scope: 1, WithLastPrice: true },
              // { pageIndex: 1, pageSize: 3000, itemType: type, itemIds: idsChunk } as any,
              { pageIndex: 1, pageSize: 3000, itemIds: idsChunk } as any,
              abortRef.current.signal
            );

            for (const r of resp.data?.data || []) {
              merged.set(`${r.itemId}|${r.unitOfMeasure}`, r);
            }
          }
        }

        setDetailsMap(merged);
      } catch (e: any) {
        if (e?.name !== 'CanceledError') console.error(e);
      } finally {
        setDetailsLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, linesKey]);

  // =========================
  // ✅ Barcode scan: find line in this PO, then increment qty
  // =========================
  useEffect(() => {
    setBarcodeError(null);
    if (!inventoryId) return;
    const h = window.setTimeout(() => barcodeInputRef.current?.focus(), 50);
    return () => window.clearTimeout(h);
  }, [inventoryId]);

  const pickBestRowForBarcode = (rows: ItemWithUomRow[]) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.find(r => r?.isPrimaryUOM) || rows[0];
  };

  const addLineByBarcode = async (rawBarcode: string) => {
    const barcode = String(rawBarcode || '').trim();
    if (!barcode) return;

    setBarcodeError(null);

    if (!inventoryId) {
      setBarcodeError(i18n.language === 'en' ? 'Select inventory first.' : 'اختر المخزن أولاً.');
      barcodeInputRef.current?.focus();
      return;
    }

    // ✅ map existing lines by itemId|uom
    const indexByKey = new Map<string, number>();
    (lines || []).forEach((l: any, idx: number) => {
      const itemId = String(l?.itemId || '');
      const uom = String(l?.uom || '');
      if (itemId && uom) indexByKey.set(`${itemId}|${uom}`, idx);
    });

    barcodeAbortRef.current?.abort();
    barcodeAbortRef.current = new AbortController();

    setBarcodeLoading(true);
    try {
      const typesToTry: ItemType[] = ['InventoryItem', 'ExpenseItem', 'FixedAssetItem', 'ServiceItem'];

      let picked: ItemWithUomRow | null = null;

      for (const type of typesToTry) {
        const resp = await fetchItemsWithUomLookup(
          { InventoryId: inventoryId, Scope: 1, WithLastPrice: true },
          // { pageIndex: 1, pageSize: 30, itemType: type, barcodeEqual: barcode } as any,
          { pageIndex: 1, pageSize: 30, barcodeEqual: barcode } as any,
          barcodeAbortRef.current.signal
        );

        const list: ItemWithUomRow[] = resp?.data?.data || [];
        const matching = list.filter(r => indexByKey.has(`${r.itemId}|${r.unitOfMeasure}`));
        const best = pickBestRowForBarcode(matching);

        if (best?.itemId && best?.unitOfMeasure) {
          picked = best;
          break;
        }
      }

      if (!picked?.itemId || !picked?.unitOfMeasure) {
        setBarcodeError(
          i18n.language === 'en' ? 'Barcode not found in this PO.' : 'هذا الباركود غير موجود ضمن أمر الشراء.'
        );
        barcodeInputRef.current?.focus();
        return;
      }

      // ✅ store for display
      setDetailsMap(prev => {
        const m = new Map(prev);
        m.set(`${picked!.itemId}|${picked!.unitOfMeasure}`, picked!);
        return m;
      });

      const key = `${picked.itemId}|${picked.unitOfMeasure}`;
      const lineIndex = indexByKey.get(key);

      if (lineIndex == null) {
        setBarcodeError(i18n.language === 'en' ? 'Item not found in lines.' : 'الصنف غير موجود ضمن البنود.');
        barcodeInputRef.current?.focus();
        return;
      }

      const current = { ...lines[lineIndex] };
      const available = Number(current.availableQtyReturn ?? 0);

      if (available <= 0) {
        setBarcodeError(
          i18n.language === 'en' ? 'No remaining quantity to return.' : 'لا توجد كمية متبقية للإرجاع لهذا الصنف.'
        );
        barcodeInputRef.current?.focus();
        return;
      }

      const nextQty = clamp(round2(Number(current.qty || 0) + 1), 0, available);
      current.selectedRow = nextQty > 0;
      current.qty = nextQty;
      current.ReturnQty = nextQty;

      recalculateReturnLineTotals(current);
      setLineAt(lineIndex, current);

      setBarcodeTerm('');
      barcodeInputRef.current?.focus();
    } catch (e: any) {
      if (e?.name !== 'CanceledError') {
        console.error(e);
        setBarcodeError(i18n.language === 'en' ? 'Failed to lookup barcode.' : 'تعذر البحث عن الباركود.');
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  // =========================
  // VAT Rate Display (Single / Multiple)
  // =========================
  const vatRateDisplay = useMemo(() => {
    const selected = (lines || []).filter(l => Number(l?.qty || 0) > 0);
    const rates = Array.from(new Set(selected.map((l: any) => clamp(num(l?.vatRate), 0, 100).toFixed(2)))).filter(
      v => v !== '0.00'
    );

    if (rates.length === 0) return '0';
    if (rates.length === 1) return rates[0];
    return i18n.language === 'en' ? 'Multiple' : 'متعدد';
  }, [lines, i18n.language]);

  const calc = useDocumentCalculator(formik, {
    lines: 'transactionsLinesList',
    headerDiscountRate: 'discountRate',
    headerDiscountAmount: 'discountAmount',
    grossAmount: 'grossAmount',
    vat: 'vat',
    netAmount: 'netAmount'
  });

  return (
    <>
      <Loading loading={false} error={null} Type='Dots'>
        <ItemUomPickerModal {...modalProps} />

        <Card>
          <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
            <div className='d-flex flex-wrap gap-4 justify-content-between'>
              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{t('الإجمالي')}:</span>
                <span className='fw-bold'>{calc.totals.linesSubtotal}</span>
              </div>
              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{t('VAT')}:</span>
                <span className='fw-bold text-warning'>{calc.totals.vatTotal}</span>
              </div>
              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{t('الصافي')}:</span>
                <span className='text-theme fs-5 fw-bold'>{calc.totals.netTotal}</span>
              </div>
            </div>
          </div>
        </Card>

        <div className='card-header'>
          <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
            <div className='d-flex align-items-center'>
              <i className='fas fa-undo fs-5 text-theme me-2'></i>
              <h6 className='mb-0 fw-semibold'>
                {t('بنود مردود المشتريات')}
                <span className='badge bg-theme rounded-pill ms-2'>{lines?.length || 0}</span>
              </h6>
            </div>

            <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
              {detailsLoading && (
                <div className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-sm text-theme me-2'></span>
                  <small className='text-muted'>{t('جاري تحميل التفاصيل...')}</small>
                </div>
              )}

              {/* ✅ barcode */}
              <div className='position-relative w-100 w-md-auto'>
                <div className='input-group input-group-sm w-100'>
                  <span className='input-group-text'>
                    <i className='fas fa-barcode text-muted'></i>
                  </span>

                  <input
                    ref={barcodeInputRef}
                    value={barcodeTerm}
                    onChange={e => {
                      setBarcodeTerm(e.target.value);
                      if (barcodeError) setBarcodeError(null);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addLineByBarcode(barcodeTerm);
                      }
                      if (e.key === 'Escape') {
                        setBarcodeTerm('');
                        setBarcodeError(null);
                      }
                    }}
                    className='form-control'
                    disabled={!inventoryId || barcodeLoading}
                    placeholder={t('امسح الباركود لتحديد بند الإرجاع')}
                    inputMode='numeric'
                  />

                  {barcodeLoading ? (
                    <span className='input-group-text'>
                      <span className='spinner-border spinner-border-sm text-theme' />
                    </span>
                  ) : (
                    <button
                      className='input-group-text'
                      onClick={() => barcodeTerm && addLineByBarcode(barcodeTerm)}
                      disabled={!barcodeTerm || barcodeLoading || !inventoryId}
                      title={t('تحديد')}
                    >
                      <i className='fas fa-search text-theme'></i>
                    </button>
                  )}
                </div>

                {barcodeError && (
                  <div className='position-absolute top-100 start-0 mt-1 w-100' style={{ zIndex: 1025 }}>
                    <div className='alert alert-danger alert-sm py-1 mb-0 d-flex align-items-center'>
                      <i className='fas fa-exclamation-circle me-2'></i>
                      <small>{barcodeError}</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-body p-0'>
          <div
            className='table-responsive'
            style={{ maxHeight: maxTableHeight, overflow: 'auto', position: 'relative' }}
          >
            <table className='table table-hover mb-0' style={{ minWidth: '1750px' }}>
              <thead
                className='shadow fw-bold'
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 101,
                  backdropFilter: 'blur(30px)'
                }}
              >
                <tr>
                  <th className='text-center small' style={{ width: '50px', minWidth: '50px' }}></th>
                  <th
                    className='text-center small shadow'
                    style={{ width: '50px', minWidth: '50px', position: 'sticky', left: 0, zIndex: 101 }}
                  >
                    #
                  </th>

                  <th className='text-center small' style={{ width: '520px', minWidth: '520px' }}>
                    {t('الصنف / الباركود')}
                  </th>

                  <th className='text-center small' style={{ width: '140px', minWidth: '140px' }}>
                    {t('الوحدة')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('الكمية المشتراة')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('المتبقي للإرجاع')}
                  </th>

                  <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
                    {t('الكمية المسترجعة')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('السعر')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('نسبة الخصم')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('قيمة الخصم')}
                  </th>

                  <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                    VAT %
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('VAT')}
                  </th>

                  <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
                    {t('Total')}
                  </th>

                  <th className='text-center small' style={{ width: '260px', minWidth: '260px' }}>
                    {t('الوصف')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {(lines || []).length > 0 ? (
                  (lines || []).map((line: any, index: number) => {
                    const info = getInfo(line);
                    const itemName = line.itemId ? getItemNameForLine(line) : '';
                    const uomName = line.itemId
                      ? getUomNameForLine(line) || line.uomName || line.unitOfMeasureName || ''
                      : line.uomName || line.unitOfMeasureName || '';

                    const barcode = info?.barcode || '';
                    const barcodeDisplay = line.itemId ? barcode || line.barcode || '-' : '-';

                    const available = Number(line.availableQtyReturn ?? 0);
                    const disabledLine = available <= 0;

                    const purchasedQty = Number(line._purchasedQty ?? line._origQty ?? 0);
                    const refundedPreviously = Math.max(0, purchasedQty - available);

                    return (
                      <tr
                        key={index}
                        className={line.selectedRow ? 'bg-theme bg-opacity-10' : ''}
                        style={{ cursor: disabledLine ? 'not-allowed' : 'default' }}
                      >
                        {/* ✅ checkbox */}
                        <td
                          className={`align-middle text-center ${
                            disabledLine ? 'bg-danger bg-opacity-25' : 'bg-theme bg-opacity-25'
                          }`}
                        >
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={!!line.selectedRow}
                            onChange={() => handleToggleSelected(index)}
                            disabled={disabledLine}
                            title={disabledLine ? t('لا توجد كمية متبقية للإرجاع') : t('تحديد للإرجاع')}
                          />
                        </td>

                        {/* ✅ # */}
                        <td className='text-center align-middle bg-body shadow' style={{ position: 'sticky', left: 0 }}>
                          <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
                            {index + 1}
                          </span>
                        </td>

                        {/* ✅ item + barcode */}
                        <td className='align-middle'>
                          <div className={`p-2 border ${disabledLine ? 'border-danger' : 'border-theme'} bg-body`}>
                            <div className='d-flex align-items-start gap-2'>
                              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                <span className='fw-bold text-truncate d-block'>
                                  {itemName ||
                                    (detailsLoading
                                      ? i18n.language === 'en'
                                        ? 'Loading...'
                                        : '...جاري التحميل'
                                      : '-')}
                                </span>

                                <small className='text-muted d-block'>
                                  {t('الوحدة')}: <span className='fw-bold text-theme'>{uomName || '-'}</span>
                                  <span className='mx-2'>•</span>
                                  {t('باركود')}: <span className='fw-bold'>{barcodeDisplay}</span>
                                </small>
                              </div>

                              {/* ✅ optional: open picker only if item is missing (legacy) */}
                              {!line.itemId && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-theme rounded-0'
                                  onClick={async () => {
                                    if (!inventoryId) {
                                      window?.alert?.(t('يجب اختيار المخزن أولاً.'));
                                      return;
                                    }
                                    const picked = await pickItemUom({
                                      title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
                                      params: { InventoryId: inventoryId, Scope: 1 },
                                      bodyDefaults: { itemType: 'InventoryItem' },
                                      pageSize: 10,
                                      searchMode: 'manual',
                                      initialSearchField: 'barcodeEqual'
                                    });
                                    if (!picked) return;

                                    const row: any = picked.row;

                                    setDetailsMap(prev => {
                                      const m = new Map(prev);
                                      m.set(`${row.itemId}|${row.unitOfMeasure}`, row);
                                      return m;
                                    });

                                    const next = {
                                      ...line,
                                      itemId: row.itemId,
                                      uom: row.unitOfMeasure,
                                      price: Number(row.lastCostPrice || row.unitPrice || 0),
                                      vatRate: Number(row.vatRate ?? row.vatPercent ?? row.taxRate ?? line.vatRate ?? 0)
                                    };
                                    setLineAt(index, next);
                                  }}
                                  title={t('اختيار صنف')}
                                >
                                  <i className='fas fa-search'></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* ✅ uom */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{uomName || '-'}</span>
                        </td>

                        {/* ✅ purchased qty (from order map) */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{formatNumber(purchasedQty)}</span>
                        </td>

                        {/* ✅ remaining */}
                        <td className='align-middle text-center'>
                          <span className={`fw-bold ${available <= 0 ? 'text-danger' : 'text-success'}`}>
                            {formatNumber(available)}
                          </span>
                        </td>

                        {/* ✅ return qty (uses qty) */}
                        <td className='align-middle'>
                          <PopoverCustom
                            childern={
                              <NumericFormat
                                name={`line-${index}-qty`}
                                value={line.qty}
                                thousandSeparator=','
                                decimalSeparator='.'
                                decimalScale={2}
                                fixedDecimalScale={false}
                                allowNegative={false}
                                className='form-control form-control-sm border-theme bg-body text-center'
                                disabled={!line.selectedRow || disabledLine}
                                isAllowed={vals => {
                                  // اسمح بالفراغ/القيم المؤقتة أثناء الكتابة
                                  if (vals.value === '' || vals.value === '.' || vals.value === ',') return true;

                                  const v = vals.floatValue;
                                  if (v == null) return true;

                                  // available من نفس scope عندك داخل map
                                  return v >= 0 && v <= available;
                                }}
                                onValueChange={v => handleReturnQtyChange(index, v.floatValue ?? 0)}
                              />
                            }
                            Header={t('تفاصيل الكمية')}
                            Body={
                              <>
                                <div className='d-flex justify-content-between'>
                                  <span className='mt-1'>{t('الكمية المتبقية للإرجاع')}:</span>
                                  <span className='text-success fw-bold fs-5 mx-2'>{available}</span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                  <span className='mt-1'>{t('الكمية التي تم إرجاعها سابقًا')}:</span>
                                  <span className='text-danger fw-bold fs-5 mx-2'>{refundedPreviously}</span>
                                </div>
                              </>
                            }
                          />
                        </td>

                        {/* ✅ price */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{formatNumber(Number(line.price || 0))}</span>
                        </td>

                        {/* ✅ discount rate */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{formatNumber(Number(line.discountRate || 0))}</span>
                        </td>

                        {/* ✅ discount amount (scaled) */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{formatNumber(Number(line.discountAmount || 0))}</span>
                        </td>

                        {/* ✅ vat rate */}
                        <td className='align-middle text-center'>
                          <span className='fw-semibold'>{formatNumber(Number(line.vatRate || 0))}</span>
                        </td>

                        {/* ✅ vat amount */}
                        <td className='align-middle fw-bold text-warning text-center'>
                          {formatNumber(Number(line.vatAmount || 0))}
                        </td>

                        {/* ✅ total with vat */}
                        <td className='align-middle text-center'>
                          <strong className='text-theme fs-6'>
                            {formatNumber(
                              line.totalWithVat != null
                                ? Number(line.totalWithVat)
                                : Number(line.total || 0) + Number(line.vatAmount || 0)
                            )}
                          </strong>
                        </td>

                        {/* ✅ description (reason) */}
                        <td className='align-middle'>
                          <Form.Control
                            autoComplete='off'
                            className='form-control form-control-sm border-theme bg-body'
                            value={line.description || ''}
                            onChange={e => handleDescChange(index, e.target.value)}
                            disabled={!line.selectedRow || disabledLine}
                            placeholder={i18n.language === 'en' ? 'Return note...' : 'سبب الإرجاع...'}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={14} className='text-center py-4 text-muted'>
                      <i className='fa fa-inbox fa-2x mb-2'></i>
                      <br />
                      {t('لا توجد بنود')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {errors?.transactionsLinesList?.length ? <div className='text-danger p-2'>* {t('مطلوب')}</div> : null}
        </div>

        {/* ========================= */}
        {/* ✅ خصم المستند (Header) */}
        {/* ========================= */}
        <div className='card-body p-3'>
          {/* <DocTotalsPanel
            discountAmountValue={Number(values.discountAmount || 0)}
            discountRateValue={Number(values.discountRate || 0)}
            netTotal={calc.totals.netTotal}
            onDiscountAmountChange={calc.onHeaderDiscountAmountChange}
            onDiscountRateChange={calc.onHeaderDiscountRateChange}
            subtotal={calc.totals.linesSubtotal}
            vatRateDisplay={vatRateDisplay as string}
            vatTotal={calc.totals.vatTotal}
            t={t}
          /> */}
        </div>
      </Loading>
    </>
  );
};

export default AddReturnLines;





// // AddReturnLines.tsx
// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { type FormikProps } from 'formik';
// import { useTranslation } from 'react-i18next';
// import { Form } from 'react-bootstrap';
// import { NumericFormat } from 'react-number-format';

// import Loading from '@components/Shared/Loader/Loading';
// import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
// import { Card } from '@components/card/card';

// import { useAppSelector } from '@store/hooks';

// // ✅ Picker (Hook + Stable Modal) - (احتياطاً لو وصلت لاينات قديمة بدون صنف)
// import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

// // ✅ API (lookup + preload details)
// import { fetchItemsWithUomLookup, type ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';

// type ItemType = 'InventoryItem' | 'ExpenseItem' | 'FixedAssetItem' | 'ServiceItem' | 'UndefinedItem';

// interface LinesProps {
//   formik: FormikProps<any>;
//   maxTableHeight?: string;
//   isUpdate?: boolean;
// }

// const chunk = <T,>(arr: T[], size: number) => {
//   const out: T[][] = [];
//   for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
//   return out;
// };

// const round2 = (v: any) => {
//   const n = Number(v || 0);
//   return Math.round((n + Number.EPSILON) * 100) / 100;
// };

// const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

// const formatNumber = (value: number) =>
//   new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

// /** ✅ قراءة مرنة لحقول اللاين (لأن أسماء الحقول تختلف أحياناً حسب الـ API) */
// const readItemId = (l: any) => String(l?.itemId ?? l?.itemID ?? l?.inventoryItemId ?? '');
// const readUom = (l: any) => String(l?.uom ?? l?.unitOfMeasure ?? l?.unitOfMeasureCode ?? '');
// const readQty = (l: any) => Number(l?.qty ?? l?.quantity ?? 0);

// const readPrice = (l: any) => Number(l?.price ?? l?.unitPrice ?? l?.lastCostPrice ?? l?.costPrice ?? 0);
// const readVatRate = (l: any) => Number(l?.vatRate ?? l?.vatPercent ?? l?.taxRate ?? 0);

// const readDiscountRate = (l: any) => Number(l?.discountRate ?? 0);
// const readDiscountAmount = (l: any) => Number(l?.discountAmount ?? l?.discountValue ?? 0);

// const buildPurchasedQtyMap = (invoice: any) => {
//   // ✅ key = itemId|uom  => purchased qty from ORIGINAL order (not the form lines)
//   const map = new Map<string, number>();

//   (invoice?.transactionsLinesList || []).forEach((l: any) => {
//     const itemId = readItemId(l);
//     const uom = readUom(l);
//     if (!itemId || !uom) return;
//     const key = `${itemId}|${uom}`;
//     map.set(key, (map.get(key) || 0) + readQty(l));
//   });

//   return map;
// };

// const buildAvailableQtyMap = (invoice: any, returns: any[]) => {
//   // ✅ key = itemId|uom
//   const map = new Map<string, number>();
//   (invoice?.transactionsLinesList || []).forEach((l: any) => {
//     const itemId = readItemId(l);
//     const uom = readUom(l);
//     if (!itemId || !uom) return;
//     const key = `${itemId}|${uom}`;
//     map.set(key, (map.get(key) || 0) + readQty(l));
//   });

//   (returns || []).forEach((ret: any) => {
//     (ret?.transactionsLinesList || []).forEach((l: any) => {
//       const itemId = readItemId(l);
//       const uom = readUom(l);
//       if (!itemId || !uom) return;
//       const key = `${itemId}|${uom}`;
//       map.set(key, (map.get(key) || 0) - readQty(l));
//     });
//   });

//   // ✅ لا نسمح بالسالب
//   for (const [k, v] of map.entries()) map.set(k, Math.max(0, v));
//   return map;
// };

// const AddReturnLines: React.FC<LinesProps> = ({ formik, maxTableHeight = '500px' }) => {
//   const { values, setFieldValue, errors } = formik;
//   const { i18n, t } = useTranslation();

//   const inventoryId: string | null = values?.inventoryId ?? null;

//   // ✅ order + previous returns are already loaded into the slice (see PurchaseReturn.tsx)
//   const { DetailsById: order } = useAppSelector(s => s.ReturnsPurchaseOrder);

//   // ✅ Picker (optional)
//   const { pickItemUom, modalProps } = useItemUomPicker();

//   // ✅ Map لعرض أسماء الأصناف والوحدات + باركود
//   const [detailsMap, setDetailsMap] = useState<Map<string, ItemWithUomRow>>(new Map());
//   const [detailsLoading, setDetailsLoading] = useState(false);
//   const abortRef = useRef<AbortController | null>(null);

//   // ✅ Barcode Scan (quick select + increment qty)
//   const barcodeInputRef = useRef<HTMLInputElement | null>(null);
//   const barcodeAbortRef = useRef<AbortController | null>(null);
//   const [barcodeTerm, setBarcodeTerm] = useState('');
//   const [barcodeLoading, setBarcodeLoading] = useState(false);
//   const [barcodeError, setBarcodeError] = useState<string | null>(null);

//   // =========================
//   // ✅ Purchased qty (from ORIGINAL order)
//   // =========================
//   const purchasedQtyMap = useMemo(() => {
//     return buildPurchasedQtyMap(order);
//   }, [order]);

//   // =========================
//   // ✅ Available qty (from order - returns)
//   // =========================
//   const availableQtyMap = useMemo(() => {
//     return buildAvailableQtyMap(order, order?.purTransactionsDtoList || []);
//   }, [order]);

//   const lines: any[] = values?.transactionsLinesList || [];

//   // =========================
//   // ✅ حساب سطور الإرجاع للعرض فقط (بدون تعديل بيانات اللاين)
//   // =========================
//   const getReturnView = (line: any) => {
//     const available = Number(line.availableQtyReturn ?? 0);

//     const qty = clamp(round2(Number(line.qty || 0)), 0, available);
//     const price = Number(line.price || 0);
//     const vatRate = clamp(round2(Number(line.vatRate || 0)), 0, 100);

//     const totalBeforeDiscount = round2(price * qty);

//     const rateVal = clamp(round2(Number(line.discountRate || 0)), 0, 100);

//     let discountApplied = 0;

//     if (rateVal > 0) {
//       discountApplied = round2((totalBeforeDiscount * rateVal) / 100);
//     } else {
//       const origQty = Number(line._origQty ?? line._purchasedQty ?? 0);
//       const origAmt = Number(line._origDiscountAmount ?? line.discountAmount ?? 0);
//       const perUnit = origQty > 0 ? origAmt / origQty : 0;
//       discountApplied = round2(perUnit * qty);
//     }

//     discountApplied = clamp(discountApplied, 0, totalBeforeDiscount);

//     const netBeforeVat = round2(totalBeforeDiscount - discountApplied);
//     const vatAmount = round2((netBeforeVat * vatRate) / 100);
//     const totalWithVat = round2(netBeforeVat + vatAmount);

//     return {
//       qty,
//       price,
//       vatRate,
//       totalBeforeDiscount,
//       discountApplied,
//       netBeforeVat,
//       vatAmount,
//       totalWithVat
//     };
//   };

//   const setLineAt = (index: number, next: any) => {
//     const updated = [...lines];
//     updated[index] = next;
//     setFieldValue('transactionsLinesList', updated);
//   };

//   // =========================
//   // ✅ One-time normalize lines (add needed fields)
//   // =========================
//   const initRef = useRef(false);

//   useEffect(() => {
//     if (initRef.current) return;

//     const srcLines = values?.transactionsLinesList || [];
//     if (!Array.isArray(srcLines) || srcLines.length === 0) return;

//     const normalized = srcLines.map((l: any) => {
//       const itemId = readItemId(l);
//       const uom = readUom(l);
//       const key = itemId && uom ? `${itemId}|${uom}` : '';

//       // ✅ purchased qty MUST come from order (not the form line)
//       const purchasedQty = key ? Number(purchasedQtyMap.get(key) ?? readQty(l)) : readQty(l);

//       return {
//         ...l,

//         itemId,
//         uom,

//         itemType: (l?.itemType || 'InventoryItem') as ItemType,

//         // ✅ keep original item description for display (fallback)
//         _itemDesc: String(l?.description || ''),

//         // ✅ display only (الكمية المشتراة)
//         _purchasedQty: purchasedQty,

//         // ✅ selection / return qty
//         selectedRow: false,

//         // ✅ IMPORTANT: qty هو كمية الإرجاع
//         qty: 0,
//         ReturnQty: 0, // alias فقط

//         // ✅ remaining qty to return
//         availableQtyReturn: key ? Number(availableQtyMap.get(key) ?? purchasedQty) : 0,

//         // ✅ originals for proportional discount when needed
//         _origQty: purchasedQty,
//         _origDiscountAmount: readDiscountAmount(l),

//         // ✅ ensure numeric fields exist
//         price: readPrice(l),
//         vatRate: readVatRate(l),
//         discountRate: readDiscountRate(l),

//         // ✅ reason/notes for return line (separate from item name)
//         description: ''
//       };
//     });

//     initRef.current = true;
//     setFieldValue('transactionsLinesList', normalized);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [availableQtyMap, purchasedQtyMap]);

//   // ✅ Update availableQtyReturn & purchased qty when order / previous returns change (without losing user inputs)
//   useEffect(() => {
//     const src = values?.transactionsLinesList || [];
//     if (!Array.isArray(src) || src.length === 0) return;

//     const updated = src.map((l: any) => {
//       const itemId = readItemId(l);
//       const uom = readUom(l);
//       const key = itemId && uom ? `${itemId}|${uom}` : '';

//       const avail = key ? Number(availableQtyMap.get(key) ?? Number(l?.availableQtyReturn ?? 0)) : 0;

//       // ✅ purchased qty comes from order map
//       const purchased = key
//         ? Number(purchasedQtyMap.get(key) ?? Number(l?._purchasedQty ?? l?._origQty ?? 0))
//         : Number(l?._purchasedQty ?? l?._origQty ?? 0);

//       const next = {
//         ...l,
//         itemId,
//         uom,
//         availableQtyReturn: avail,
//         _purchasedQty: purchased,
//         _origQty: purchased
//       };

//       // ✅ clamp qty فقط (بدون لمس أي حقول أخرى)
//       const rq = clamp(round2(Number(next.qty || 0)), 0, avail);
//       next.qty = rq;
//       next.ReturnQty = rq;

//       return next;
//     });

//     setFieldValue('transactionsLinesList', updated);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [availableQtyMap, purchasedQtyMap]);

//   // =========================
//   // ✅ Totals for header (UI + optional sync to form fields)
//   // =========================
//   const uiTotalsRaw = useMemo(() => {
//     let subtotal = 0;
//     let vat = 0;
//     let net = 0;

//     for (const l of lines || []) {
//       const v = getReturnView(l);
//       if (v.qty <= 0) continue;

//       subtotal += v.totalBeforeDiscount;
//       vat += v.vatAmount;
//       net += v.totalWithVat;
//     }

//     return {
//       subtotal: round2(subtotal),
//       vat: round2(vat),
//       net: round2(net)
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [lines]);

//   const uiTotals = useMemo(
//     () => ({
//       linesSubtotal: formatNumber(uiTotalsRaw.subtotal),
//       vatTotal: formatNumber(uiTotalsRaw.vat),
//       netTotal: formatNumber(uiTotalsRaw.net)
//     }),
//     [uiTotalsRaw]
//   );

//   // ✅ إذا تبغى تزامن حقول الهيدر في الفورم (اختياري)
//   useEffect(() => {
//     // لا نعمل setFieldValue إلا إذا اختلفت لتجنب حلقات
//     const g = Number(values?.grossAmount || 0);
//     const v = Number(values?.vat || 0);
//     const n = Number(values?.netAmount || 0);

//     if (round2(g) !== uiTotalsRaw.subtotal) setFieldValue('grossAmount', uiTotalsRaw.subtotal);
//     if (round2(v) !== uiTotalsRaw.vat) setFieldValue('vat', uiTotalsRaw.vat);
//     if (round2(n) !== uiTotalsRaw.net) setFieldValue('netAmount', uiTotalsRaw.net);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [uiTotalsRaw.subtotal, uiTotalsRaw.vat, uiTotalsRaw.net]);

//   // =========================
//   // Display helpers
//   // =========================
//   const getInfo = (line: any) => {
//     if (!line?.itemId || !line?.uom) return null;
//     return detailsMap.get(`${line.itemId}|${line.uom}`) || null;
//   };

//   const getItemNameForLine = (line: any) => {
//     const info = getInfo(line);
//     const isEn = i18n.language === 'en';
//     const name = isEn ? info?.name || info?.name2 : info?.name2 || info?.name;
//     return name || line?._itemDesc || (line?.itemId ? `ID: ${line.itemId}` : '');
//   };

//   const getUomNameForLine = (line: any) => {
//     const info = getInfo(line);
//     const isEn = i18n.language === 'en';
//     const uomName = isEn
//       ? info?.unitOfMeasureName || info?.unitOfMeasureName2
//       : info?.unitOfMeasureName2 || info?.unitOfMeasureName;
//     return uomName || '';
//   };

//   // =========================
//   // Handlers
//   // =========================
//   const handleToggleSelected = (index: number) => {
//     const l = lines[index];
//     if (!l) return;

//     const available = Number(l.availableQtyReturn ?? 0);
//     if (available <= 0) return;

//     const next = { ...l, selectedRow: !l.selectedRow };

//     if (!next.selectedRow) {
//       next.qty = 0;
//       next.ReturnQty = 0;
//     } else {
//       if (!Number(next.qty) && available > 0) next.qty = Math.min(1, available);
//       next.ReturnQty = next.qty;
//     }

//     setLineAt(index, next);
//   };

//   // ✅ المطلوب: دالة تغيير الكمية تغيّر qty فقط (و ReturnQty alias)
//   const handleReturnQtyChange = (index: number, value: any) => {
//     const l = lines[index];
//     if (!l) return;

//     const available = Number(l.availableQtyReturn ?? 0);
//     const qty = clamp(round2(value), 0, available);

//     const next = { ...l, qty, ReturnQty: qty };
//     setLineAt(index, next);
//   };

//   const handleDescChange = (index: number, desc: string) => {
//     const l = lines[index];
//     if (!l) return;
//     setLineAt(index, { ...l, description: desc });
//   };

//   // =========================
//   // ✅ Load missing details (names/barcodes/uoms) - same idea as PO
//   // =========================
//   const linesKey = useMemo(() => {
//     return (lines || []).map(l => `${l?.itemType || ''}:${l?.itemId || ''}:${l?.uom || ''}`).join('|');
//   }, [lines]);

//   useEffect(() => {
//     const run = async () => {
//       if (!inventoryId) return;

//       const valid = (lines || []).filter(l => l?.itemId && l?.uom);
//       if (valid.length === 0) return;

//       const missingByType = new Map<string, Set<string>>();
//       for (const l of valid) {
//         const key = `${l.itemId}|${l.uom}`;
//         if (detailsMap.has(key)) continue;

//         const type = String(l.itemType || 'InventoryItem');
//         if (!missingByType.has(type)) missingByType.set(type, new Set());
//         missingByType.get(type)!.add(String(l.itemId));
//       }

//       if (missingByType.size === 0) return;

//       abortRef.current?.abort();
//       abortRef.current = new AbortController();

//       setDetailsLoading(true);
//       try {
//         const merged = new Map(detailsMap);

//         for (const [type, idsSet] of missingByType.entries()) {
//           const itemIds = Array.from(idsSet);
//           for (const idsChunk of chunk(itemIds, 200)) {
//             const resp = await fetchItemsWithUomLookup(
//               { InventoryId: inventoryId, Scope: 1, WithLastPrice: true },
//               { pageIndex: 1, pageSize: 3000, itemIds: idsChunk } as any,
//               abortRef.current.signal
//             );

//             for (const r of resp.data?.data || []) {
//               merged.set(`${r.itemId}|${r.unitOfMeasure}`, r);
//             }
//           }
//         }

//         setDetailsMap(merged);
//       } catch (e: any) {
//         if (e?.name !== 'CanceledError') console.error(e);
//       } finally {
//         setDetailsLoading(false);
//       }
//     };

//     run();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [inventoryId, linesKey]);

//   // =========================
//   // ✅ Barcode scan: find line in this PO, then increment qty
//   // =========================
//   useEffect(() => {
//     setBarcodeError(null);
//     if (!inventoryId) return;
//     const h = window.setTimeout(() => barcodeInputRef.current?.focus(), 50);
//     return () => window.clearTimeout(h);
//   }, [inventoryId]);

//   const pickBestRowForBarcode = (rows: ItemWithUomRow[]) => {
//     if (!Array.isArray(rows) || rows.length === 0) return null;
//     return rows.find(r => r?.isPrimaryUOM) || rows[0];
//   };

//   const addLineByBarcode = async (rawBarcode: string) => {
//     const barcode = String(rawBarcode || '').trim();
//     if (!barcode) return;

//     setBarcodeError(null);

//     if (!inventoryId) {
//       setBarcodeError(i18n.language === 'en' ? 'Select inventory first.' : 'اختر المخزن أولاً.');
//       barcodeInputRef.current?.focus();
//       return;
//     }

//     // ✅ map existing lines by itemId|uom
//     const indexByKey = new Map<string, number>();
//     (lines || []).forEach((l: any, idx: number) => {
//       const itemId = String(l?.itemId || '');
//       const uom = String(l?.uom || '');
//       if (itemId && uom) indexByKey.set(`${itemId}|${uom}`, idx);
//     });

//     barcodeAbortRef.current?.abort();
//     barcodeAbortRef.current = new AbortController();

//     setBarcodeLoading(true);
//     try {
//       const typesToTry: ItemType[] = ['InventoryItem', 'ExpenseItem', 'FixedAssetItem', 'ServiceItem'];

//       let picked: ItemWithUomRow | null = null;

//       for (const type of typesToTry) {
//         const resp = await fetchItemsWithUomLookup(
//           { InventoryId: inventoryId, Scope: 1, WithLastPrice: true },
//           { pageIndex: 1, pageSize: 30, barcodeEqual: barcode } as any,
//           barcodeAbortRef.current.signal
//         );

//         const list: ItemWithUomRow[] = resp?.data?.data || [];
//         const matching = list.filter(r => indexByKey.has(`${r.itemId}|${r.unitOfMeasure}`));
//         const best = pickBestRowForBarcode(matching);

//         if (best?.itemId && best?.unitOfMeasure) {
//           picked = best;
//           break;
//         }
//       }

//       if (!picked?.itemId || !picked?.unitOfMeasure) {
//         setBarcodeError(i18n.language === 'en' ? 'Barcode not found in this PO.' : 'هذا الباركود غير موجود ضمن أمر الشراء.');
//         barcodeInputRef.current?.focus();
//         return;
//       }

//       // ✅ store for display
//       setDetailsMap(prev => {
//         const m = new Map(prev);
//         m.set(`${picked!.itemId}|${picked!.unitOfMeasure}`, picked!);
//         return m;
//       });

//       const key = `${picked.itemId}|${picked.unitOfMeasure}`;
//       const lineIndex = indexByKey.get(key);

//       if (lineIndex == null) {
//         setBarcodeError(i18n.language === 'en' ? 'Item not found in lines.' : 'الصنف غير موجود ضمن البنود.');
//         barcodeInputRef.current?.focus();
//         return;
//       }

//       const current = { ...lines[lineIndex] };
//       const available = Number(current.availableQtyReturn ?? 0);

//       if (available <= 0) {
//         setBarcodeError(i18n.language === 'en' ? 'No remaining quantity to return.' : 'لا توجد كمية متبقية للإرجاع لهذا الصنف.');
//         barcodeInputRef.current?.focus();
//         return;
//       }

//       const nextQty = clamp(round2(Number(current.qty || 0) + 1), 0, available);
//       current.selectedRow = true;
//       current.qty = nextQty;
//       current.ReturnQty = nextQty;

//       setLineAt(lineIndex, current);

//       setBarcodeTerm('');
//       barcodeInputRef.current?.focus();
//     } catch (e: any) {
//       if (e?.name !== 'CanceledError') {
//         console.error(e);
//         setBarcodeError(i18n.language === 'en' ? 'Failed to lookup barcode.' : 'تعذر البحث عن الباركود.');
//       }
//     } finally {
//       setBarcodeLoading(false);
//     }
//   };

//   return (
//     <>
//       <Loading loading={false} error={null} Type='Dots'>
//         <ItemUomPickerModal {...modalProps} />

//         <Card>
//           <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
//             <div className='d-flex flex-wrap gap-4 justify-content-between'>
//               <div className='d-flex gap-2 align-items-center'>
//                 <span className='fw-bold'>{t('الإجمالي')}:</span>
//                 <span className='fw-bold'>{uiTotals.linesSubtotal}</span>
//               </div>
//               <div className='d-flex gap-2 align-items-center'>
//                 <span className='fw-bold'>{t('VAT')}:</span>
//                 <span className='fw-bold text-warning'>{uiTotals.vatTotal}</span>
//               </div>
//               <div className='d-flex gap-2 align-items-center'>
//                 <span className='fw-bold'>{t('الصافي')}:</span>
//                 <span className='text-theme fs-5 fw-bold'>{uiTotals.netTotal}</span>
//               </div>
//             </div>
//           </div>
//         </Card>

//         <div className='card-header'>
//           <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
//             <div className='d-flex align-items-center'>
//               <i className='fas fa-undo fs-5 text-theme me-2'></i>
//               <h6 className='mb-0 fw-semibold'>
//                 {t('بنود مردود المشتريات')}
//                 <span className='badge bg-theme rounded-pill ms-2'>{lines?.length || 0}</span>
//               </h6>
//             </div>

//             <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
//               {detailsLoading && (
//                 <div className='d-flex align-items-center'>
//                   <span className='spinner-border spinner-border-sm text-theme me-2'></span>
//                   <small className='text-muted'>{t('جاري تحميل التفاصيل...')}</small>
//                 </div>
//               )}

//               {/* ✅ barcode */}
//               <div className='position-relative w-100 w-md-auto'>
//                 <div className='input-group input-group-sm w-100'>
//                   <span className='input-group-text'>
//                     <i className='fas fa-barcode text-muted'></i>
//                   </span>

//                   <input
//                     ref={barcodeInputRef}
//                     value={barcodeTerm}
//                     onChange={e => {
//                       setBarcodeTerm(e.target.value);
//                       if (barcodeError) setBarcodeError(null);
//                     }}
//                     onKeyDown={e => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         addLineByBarcode(barcodeTerm);
//                       }
//                       if (e.key === 'Escape') {
//                         setBarcodeTerm('');
//                         setBarcodeError(null);
//                       }
//                     }}
//                     className='form-control'
//                     disabled={!inventoryId || barcodeLoading}
//                     placeholder={t('امسح الباركود لتحديد بند الإرجاع')}
//                     inputMode='numeric'
//                   />

//                   {barcodeLoading ? (
//                     <span className='input-group-text'>
//                       <span className='spinner-border spinner-border-sm text-theme' />
//                     </span>
//                   ) : (
//                     <button
//                       className='input-group-text'
//                       onClick={() => barcodeTerm && addLineByBarcode(barcodeTerm)}
//                       disabled={!barcodeTerm || barcodeLoading || !inventoryId}
//                       title={t('تحديد')}
//                     >
//                       <i className='fas fa-search text-theme'></i>
//                     </button>
//                   )}
//                 </div>

//                 {barcodeError && (
//                   <div className='position-absolute top-100 start-0 mt-1 w-100' style={{ zIndex: 1025 }}>
//                     <div className='alert alert-danger alert-sm py-1 mb-0 d-flex align-items-center'>
//                       <i className='fas fa-exclamation-circle me-2'></i>
//                       <small>{barcodeError}</small>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className='card-body p-0'>
//           <div className='table-responsive' style={{ maxHeight: maxTableHeight, overflow: 'auto', position: 'relative' }}>
//             <table className='table table-hover mb-0' style={{ minWidth: '1750px' }}>
//               <thead
//                 className='shadow fw-bold'
//                 style={{
//                   position: 'sticky',
//                   top: 0,
//                   zIndex: 101,
//                   backdropFilter: 'blur(30px)'
//                 }}
//               >
//                 <tr>
//                   <th className='text-center small' style={{ width: '50px', minWidth: '50px' }}></th>
//                   <th
//                     className='text-center small shadow'
//                     style={{ width: '50px', minWidth: '50px', position: 'sticky', left: 0, zIndex: 101 }}
//                   >
//                     #
//                   </th>

//                   <th className='text-center small' style={{ width: '520px', minWidth: '520px' }}>
//                     {t('الصنف / الباركود')}
//                   </th>

//                   <th className='text-center small' style={{ width: '140px', minWidth: '140px' }}>
//                     {t('الوحدة')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('الكمية المشتراة')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('المتبقي للإرجاع')}
//                   </th>

//                   <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
//                     {t('الكمية المسترجعة')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('السعر')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('نسبة الخصم')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('قيمة الخصم')}
//                   </th>

//                   <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
//                     VAT %
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('VAT')}
//                   </th>

//                   <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
//                     {t('Total')}
//                   </th>

//                   <th className='text-center small' style={{ width: '260px', minWidth: '260px' }}>
//                     {t('الوصف')}
//                   </th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {(lines || []).length > 0 ? (
//                   (lines || []).map((line: any, index: number) => {
//                     const info = getInfo(line);
//                     const itemName = line.itemId ? getItemNameForLine(line) : '';
//                     const uomName = line.itemId
//                       ? getUomNameForLine(line) || line.uomName || line.unitOfMeasureName || ''
//                       : line.uomName || line.unitOfMeasureName || '';

//                     const barcode = info?.barcode || '';
//                     const barcodeDisplay = line.itemId ? barcode || line.barcode || '-' : '-';

//                     const available = Number(line.availableQtyReturn ?? 0);
//                     const disabledLine = available <= 0;

//                     const purchasedQty = Number(line._purchasedQty ?? line._origQty ?? 0);
//                     const refundedPreviously = Math.max(0, purchasedQty - available);

//                     const view = getReturnView(line);

//                     return (
//                       <tr
//                         key={index}
//                         className={line.selectedRow ? 'bg-theme bg-opacity-10' : ''}
//                         style={{ cursor: disabledLine ? 'not-allowed' : 'default' }}
//                       >
//                         {/* ✅ checkbox */}
//                         <td
//                           className={`align-middle text-center ${
//                             disabledLine ? 'bg-danger bg-opacity-25' : 'bg-theme bg-opacity-25'
//                           }`}
//                         >
//                           <input
//                             type='checkbox'
//                             className='form-check-input'
//                             checked={!!line.selectedRow}
//                             onChange={() => handleToggleSelected(index)}
//                             disabled={disabledLine}
//                             title={disabledLine ? t('لا توجد كمية متبقية للإرجاع') : t('تحديد للإرجاع')}
//                           />
//                         </td>

//                         {/* ✅ # */}
//                         <td className='text-center align-middle bg-body shadow' style={{ position: 'sticky', left: 0 }}>
//                           <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
//                             {index + 1}
//                           </span>
//                         </td>

//                         {/* ✅ item + barcode */}
//                         <td className='align-middle'>
//                           <div className={`p-2 border ${disabledLine ? 'border-danger' : 'border-theme'} bg-body`}>
//                             <div className='d-flex align-items-start gap-2'>
//                               <div className='flex-grow-1' style={{ minWidth: 0 }}>
//                                 <span className='fw-bold text-truncate d-block'>
//                                   {itemName ||
//                                     (detailsLoading
//                                       ? i18n.language === 'en'
//                                         ? 'Loading...'
//                                         : '...جاري التحميل'
//                                       : '-')}
//                                 </span>

//                                 <small className='text-muted d-block'>
//                                   {t('الوحدة')}: <span className='fw-bold text-theme'>{uomName || '-'}</span>
//                                   <span className='mx-2'>•</span>
//                                   {t('باركود')}: <span className='fw-bold'>{barcodeDisplay}</span>
//                                 </small>
//                               </div>

//                               {/* ✅ optional: open picker only if item is missing (legacy) */}
//                               {!line.itemId && (
//                                 <button
//                                   type='button'
//                                   className='btn btn-sm btn-theme rounded-0'
//                                   onClick={async () => {
//                                     if (!inventoryId) {
//                                       window?.alert?.(t('يجب اختيار المخزن أولاً.'));
//                                       return;
//                                     }
//                                     const picked = await pickItemUom({
//                                       title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
//                                       params: { InventoryId: inventoryId, Scope: 1 },
//                                       bodyDefaults: { itemType: 'InventoryItem' },
//                                       pageSize: 10,
//                                       searchMode: 'manual',
//                                       initialSearchField: 'barcodeEqual'
//                                     });
//                                     if (!picked) return;

//                                     const row: any = picked.row;

//                                     setDetailsMap(prev => {
//                                       const m = new Map(prev);
//                                       m.set(`${row.itemId}|${row.unitOfMeasure}`, row);
//                                       return m;
//                                     });

//                                     const next = {
//                                       ...line,
//                                       itemId: row.itemId,
//                                       uom: row.unitOfMeasure,
//                                       price: Number(row.lastCostPrice || row.unitPrice || 0),
//                                       vatRate: Number(row.vatRate ?? row.vatPercent ?? row.taxRate ?? line.vatRate ?? 0)
//                                     };
//                                     setLineAt(index, next);
//                                   }}
//                                   title={t('اختيار صنف')}
//                                 >
//                                   <i className='fas fa-search'></i>
//                                 </button>
//                               )}
//                             </div>
//                           </div>
//                         </td>

//                         {/* ✅ uom */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{uomName || '-'}</span>
//                         </td>

//                         {/* ✅ purchased qty (from order map) */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{formatNumber(purchasedQty)}</span>
//                         </td>

//                         {/* ✅ remaining */}
//                         <td className='align-middle text-center'>
//                           <span className={`fw-bold ${available <= 0 ? 'text-danger' : 'text-success'}`}>
//                             {formatNumber(available)}
//                           </span>
//                         </td>

//                         {/* ✅ return qty (changes qty only) */}
//                         <td className='align-middle'>
//                           <PopoverCustom
//                             childern={
//                               <NumericFormat
//                                 name={`line-${index}-qty`}
//                                 value={line.qty}
//                                 thousandSeparator=','
//                                 decimalSeparator='.'
//                                 decimalScale={2}
//                                 fixedDecimalScale={false}
//                                 allowNegative={false}
//                                 className='form-control form-control-sm border-theme bg-body text-center'
//                                 disabled={!line.selectedRow || disabledLine}
//                                 isAllowed={vals => {
//                                   if (vals.value === '' || vals.value === '.' || vals.value === ',') return true;
//                                   const v = vals.floatValue;
//                                   if (v == null) return true;
//                                   return v >= 0 && v <= available;
//                                 }}
//                                 onValueChange={v => handleReturnQtyChange(index, v.floatValue ?? 0)}
//                               />
//                             }
//                             Header={t('تفاصيل الكمية')}
//                             Body={
//                               <>
//                                 <div className='d-flex justify-content-between'>
//                                   <span className='mt-1'>{t('الكمية المتبقية للإرجاع')}:</span>
//                                   <span className='text-success fw-bold fs-5 mx-2'>{available}</span>
//                                 </div>
//                                 <div className='d-flex justify-content-between'>
//                                   <span className='mt-1'>{t('الكمية التي تم إرجاعها سابقًا')}:</span>
//                                   <span className='text-danger fw-bold fs-5 mx-2'>{refundedPreviously}</span>
//                                 </div>
//                               </>
//                             }
//                           />
//                         </td>

//                         {/* ✅ price */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{formatNumber(Number(line.price || 0))}</span>
//                         </td>

//                         {/* ✅ discount rate */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{formatNumber(Number(line.discountRate || 0))}</span>
//                         </td>

//                         {/* ✅ discount amount (computed for view only) */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{formatNumber(Number(view.discountApplied || 0))}</span>
//                         </td>

//                         {/* ✅ vat rate */}
//                         <td className='align-middle text-center'>
//                           <span className='fw-semibold'>{formatNumber(Number(line.vatRate || 0))}</span>
//                         </td>

//                         {/* ✅ vat amount (computed for view only) */}
//                         <td className='align-middle fw-bold text-warning text-center'>
//                           {formatNumber(Number(view.vatAmount || 0))}
//                         </td>

//                         {/* ✅ total with vat (computed for view only) */}
//                         <td className='align-middle text-center'>
//                           <strong className='text-theme fs-6'>{formatNumber(Number(view.totalWithVat || 0))}</strong>
//                         </td>

//                         {/* ✅ description (reason) */}
//                         <td className='align-middle'>
//                           <Form.Control
//                             autoComplete='off'
//                             className='form-control form-control-sm border-theme bg-body'
//                             value={line.description || ''}
//                             onChange={e => handleDescChange(index, e.target.value)}
//                             disabled={!line.selectedRow || disabledLine}
//                             placeholder={i18n.language === 'en' ? 'Return note...' : 'سبب الإرجاع...'}
//                           />
//                         </td>
//                       </tr>
//                     );
//                   })
//                 ) : (
//                   <tr>
//                     <td colSpan={14} className='text-center py-4 text-muted'>
//                       <i className='fa fa-inbox fa-2x mb-2'></i>
//                       <br />
//                       {t('لا توجد بنود')}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {errors?.transactionsLinesList?.length ? <div className='text-danger p-2'>* {t('مطلوب')}</div> : null}
//         </div>

//         {/* ========================= */}
//         {/* ✅ خصم المستند (Header) */}
//         {/* ========================= */}
//         <div className='card-body p-3'>{/* intentionally left empty */}</div>
//       </Loading>
//     </>
//   );
// };

// export default AddReturnLines;
